import { supabase } from '../lib/supabase';

export const notificationService = {
  /**
   * Get all notifications for current user filtered by role
   * @param {string} role - 'customer' or 'agent'
   * @returns {Promise<Array>} Array of notifications
   */
  async getAll(role) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', user?.id)?.eq('role', role)?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(notification => ({
      id: notification?.id,
      userId: notification?.user_id,
      role: notification?.role,
      type: notification?.type,
      title: notification?.title,
      message: notification?.message,
      actionUrl: notification?.action_url,
      entityId: notification?.entity_id,
      entityType: notification?.entity_type,
      isRead: notification?.is_read,
      readAt: notification?.read_at,
      createdAt: notification?.created_at,
      metadata: notification?.metadata
    })) || [];
  },

  /**
   * Get unread count for current user by role
   * @param {string} role - 'customer' or 'agent'
   * @returns {Promise<number>} Unread notification count
   */
  async getUnreadCount(role) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return 0;

    const { count, error } = await supabase?.from('notifications')?.select('*', { count: 'exact', head: true })?.eq('user_id', user?.id)?.eq('role', role)?.eq('is_read', false);

    if (error) throw error;
    return count || 0;
  },

  /**
   * Mark single notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} Updated notification
   */
  async markAsRead(notificationId) {
    const { data, error } = await supabase?.from('notifications')?.update({
        is_read: true,
        read_at: new Date()?.toISOString()
      })?.eq('id', notificationId)?.select()?.single();

    if (error) throw error;

    // Convert to camelCase
    return {
      id: data?.id,
      userId: data?.user_id,
      role: data?.role,
      type: data?.type,
      title: data?.title,
      message: data?.message,
      actionUrl: data?.action_url,
      entityId: data?.entity_id,
      entityType: data?.entity_type,
      isRead: data?.is_read,
      readAt: data?.read_at,
      createdAt: data?.created_at,
      metadata: data?.metadata
    };
  },

  /**
   * Mark all notifications as read for current user
   * @param {string} role - 'customer' or 'agent'
   * @returns {Promise<void>}
   */
  async markAllAsRead(role) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('notifications')?.update({
        is_read: true,
        read_at: new Date()?.toISOString()
      })?.eq('user_id', user?.id)?.eq('role', role)?.eq('is_read', false);

    if (error) throw error;
  },

  /**
   * Subscribe to real-time notification updates
   * @param {string} userId - User ID
   * @param {string} role - 'customer' or 'agent'
   * @param {Function} callback - Callback function for updates
   * @returns {Object} Supabase channel subscription
   */
  subscribeToNotifications(userId, role, callback) {
    const channel = supabase?.channel(`notifications_${userId}_${role}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          // Only trigger callback if notification matches the role
          if (payload?.new?.role === role || payload?.old?.role === role) {
            callback(payload);
          }
        }
      )?.subscribe();

    return channel;
  }
};